// src/pages/VehicleV2/BillGenerationListing.jsx
// Slim table + view modal to avoid overflow.
// View modal shows full row; Open Bill still goes to BillGeneration.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaEye,
  FaTimes,
  FaFileExport,
  FaSyncAlt,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/billGenerationListing`;

const HIRE_TYPE = "H";

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const clean = (v) => String(v ?? "").trim();

const wildcardOrValue = (v) => {
  const s = clean(v);
  if (!s || s === "Select" || s === "All") return "%";
  return s;
};

const defaultFilters = {
  storeId: "Select",
  vehTypeId: "Select",
  vehSubTypeId: "Select",
  vehBasis: "Select",
  vehicleCode: "",
  vehicleNumber: "",
  vehicleName: "",
};

const VehicleBillGenerationListing = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("Branchid") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS = getLS("userId") || getLS("loginuserid");

  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(() => toStr(branchTypeLS || "HO"), [branchTypeLS]);
  const userId = useMemo(() => toStr(userIdLS), [userIdLS]);
  const loginUserId = useMemo(() => {
    const sid =
      typeof window !== "undefined" ? sessionStorage.getItem("loginUserId") : "";
    return toStr(sid || userId);
  }, [userId]);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const [hasRights] = useState(true);
  const [addRights] = useState(true);

  const [stores, setStores] = useState([{ id: "Select", name: "Select" }]);
  const [vehicleTypes, setVehicleTypes] = useState([{ id: "Select", name: "Select" }]);
  const [vehicleSubTypes, setVehicleSubTypes] = useState([{ id: "Select", name: "Select" }]);
  const [vehicleBasis, setVehicleBasis] = useState([{ id: "Select", name: "Select" }]);

  const [filters, setFilters] = useState(defaultFilters);
  const [showFilters, setShowFilters] = useState(true);
  const [list, setList] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [codeSuggestions, setCodeSuggestions] = useState([]);
  const [numSuggestions, setNumSuggestions] = useState([]);
  const [nameSuggestions, setNameSuggestions] = useState([]);

  const isBusy = pageLoading || dropdownLoading || gridLoading || exporting;

  const totalRecords = list.length;
  const totalPages = useMemo(() => {
    const t = Math.ceil((totalRecords || 0) / limit);
    return t <= 0 ? 1 : t;
  }, [totalRecords, limit]);

  const pagedList = useMemo(() => {
    const start = (page - 1) * limit;
    return list.slice(start, start + limit);
  }, [list, page, limit]);

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const suggestionBoxClass = `absolute z-20 mt-1 w-full rounded-lg border shadow-lg ${
    isDark ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"
  }`;

  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const resetSuggestions = useCallback(() => {
    setCodeSuggestions([]);
    setNumSuggestions([]);
    setNameSuggestions([]);
  }, []);

  const resetFiltersOnly = useCallback(() => {
    setFilters(defaultFilters);
    resetSuggestions();
  }, [resetSuggestions]);

  const buildListParams = useCallback(
    (override = {}) => ({
      branchId,
      branchType,
      loginUserId,
      type: HIRE_TYPE,
      storeId: override.storeId ?? filters.storeId,
      vehTypeId: override.vehTypeId ?? filters.vehTypeId,
      vehSubTypeId: override.vehSubTypeId ?? filters.vehSubTypeId,
      vehBasis: override.vehBasis ?? filters.vehBasis,
      vehicleCode: override.vehicleCode ?? filters.vehicleCode,
      vehicleNumber: override.vehicleNumber ?? filters.vehicleNumber,
      vehicleName: override.vehicleName ?? filters.vehicleName,
    }),
    [branchId, branchType, loginUserId, filters]
  );

  const loadDropdowns = useCallback(async () => {
    setDropdownLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/dropdowns`, {
        params: { branchId, loginUserId },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load dropdowns");
      const makeOptions = (arr) => [
        { id: "Select", name: "Select" },
        ...(arr || []).map((x) => ({ id: String(x.id), name: x.name })),
      ];
      setStores(makeOptions(data.stores));
      setVehicleTypes(makeOptions(data.vehicleTypes));
      setVehicleSubTypes(makeOptions(data.vehicleSubTypes));
      setVehicleBasis(makeOptions(data.vehicleBasis));
    } finally {
      setDropdownLoading(false);
    }
  }, [branchId, loginUserId, commonHeaders]);

  const loadList = useCallback(
    async ({ silent = false, overrideFilters = null } = {}) => {
      if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
      if (!canUse(toInt(branchId), "branchId missing")) return;

      try {
        setGridLoading(true);
        setHasSearched(true);
        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(overrideFilters || {}),
          headers: commonHeaders,
        });
        if (!data?.success) {
          setList([]);
          setPage(1);
          if (!silent) toast.warn(data?.message || "No data found");
          return;
        }
        setList(data.list || []);
        setPage(1);
        if (!silent) toast.success(`Found ${data.list?.length || 0} record(s)`);
      } catch (err) {
        console.error(err);
        setList([]);
        setPage(1);
        toast.error(err?.response?.data?.message || err.message || "Failed to load list");
      } finally {
        setGridLoading(false);
      }
    },
    [loginUserId, branchId, commonHeaders, buildListParams, canUse]
  );

  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;
    try {
      setPageLoading(true);
      await loadDropdowns();
      await loadList({ silent: true });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Initialization failed");
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, canUse, loadDropdowns, loadList]);

  const fetchSuggestions = useCallback(
    async (kind, prefix) => {
      const pfx = clean(prefix);
      if (!pfx) return [];
      const params = {
        prefix: pfx,
        branchId: branchType === "HO" ? "%" : branchId,
        branchType,
        loginUserId,
        type: HIRE_TYPE,
        vehType: wildcardOrValue(filters.vehTypeId),
        subType: wildcardOrValue(filters.vehSubTypeId),
        vehBasis: wildcardOrValue(filters.vehBasis),
        store: wildcardOrValue(filters.storeId),
      };
      let url = "";
      if (kind === "code") url = `${API_BASE}/autocomplete/vehicleCode`;
      if (kind === "number") url = `${API_BASE}/autocomplete/vehicleNumber`;
      if (kind === "name") url = `${API_BASE}/autocomplete/vehicleName`;
      if (!url) return [];
      const { data } = await axios.get(url, { params, headers: commonHeaders });
      return data?.items || [];
    },
    [branchId, branchType, loginUserId, filters, commonHeaders]
  );

  const onFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  }, []);

  const handleSearch = useCallback(async () => {
    setPage(1);
    await loadList({ silent: false });
  }, [loadList]);

  const handlePage = useCallback(
    (p) => {
      if (p < 1 || p > totalPages || isBusy) return;
      setPage(p);
    },
    [totalPages, isBusy]
  );

  const handleClearFilters = useCallback(async () => {
    resetFiltersOnly();
    await loadList({ silent: false, overrideFilters: defaultFilters });
  }, [resetFiltersOnly, loadList]);

  const handleRefresh = useCallback(async () => {
    resetSuggestions();
    await loadInit();
  }, [resetSuggestions, loadInit]);

  const exportExcel = useCallback(async () => {
    try {
      setExporting(true);
      toast.info("Export feature can be connected to backend /export endpoint");
    } catch (err) {
      toast.error(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }, []);

  const openBillGeneration = useCallback((vehId) => {
    navigate(`/vehicle/BillGeneration?VehID=${vehId}&brtype=${HIRE_TYPE}`);
  }, [navigate]);

  const handleSuggestionInput = useCallback(
    async (kind, value) => {
      if (kind === "code") setFilters((p) => ({ ...p, vehicleCode: value }));
      else if (kind === "number") setFilters((p) => ({ ...p, vehicleNumber: value }));
      else if (kind === "name") setFilters((p) => ({ ...p, vehicleName: value }));

      if (value.length < 2) {
        if (kind === "code") setCodeSuggestions([]);
        if (kind === "number") setNumSuggestions([]);
        if (kind === "name") setNameSuggestions([]);
        return;
      }

      try {
        const items = (await fetchSuggestions(kind, value)).slice(0, 8);
        if (kind === "code") setCodeSuggestions(items);
        if (kind === "number") setNumSuggestions(items);
        if (kind === "name") setNameSuggestions(items);
      } catch {
        if (kind === "code") setCodeSuggestions([]);
        if (kind === "number") setNumSuggestions([]);
        if (kind === "name") setNameSuggestions([]);
      }
    },
    [fetchSuggestions]
  );

  const selectSuggestion = useCallback((kind, value) => {
    if (kind === "code") {
      setFilters((p) => ({ ...p, vehicleCode: value }));
      setCodeSuggestions([]);
    } else if (kind === "number") {
      setFilters((p) => ({ ...p, vehicleNumber: value }));
      setNumSuggestions([]);
    } else if (kind === "name") {
      setFilters((p) => ({ ...p, vehicleName: value }));
      setNameSuggestions([]);
    }
  }, []);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  if (!hasRights) {
    return (
      <VehicleLayoutWrapper>
        <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
          <HeaderBar
            title={<span className="font-dmserif text-3xl">BILL GENERATION</span>}
            setSkin={setSkin}
            currentSkin={currentSkin}
          />
          <div
            className={`p-6 mt-6 rounded-2xl shadow-xl border ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
                : "bg-white text-black"
            }`}
          >
            You don&apos;t have rights to access this page.
          </div>
        </div>
      </VehicleLayoutWrapper>
    );
  }

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BILL GENERATION LISTING</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Bill Generation Listing
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {branchId || "-"} • User ID: {loginUserId || "-"} • Hire Vehicle • {list.length} Records
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={exportExcel}
                disabled={isBusy}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                  isBusy ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaFileExport />
                {exporting ? "Exporting..." : "Export"}
              </button>
              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt />
                Refresh
              </button>
              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    disabled={isBusy}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleSearch}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {gridLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Store</label>
                  <select name="storeId" value={filters.storeId} onChange={onFilterChange} className={fieldClass} disabled={dropdownLoading}>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Vehicle Type</label>
                  <select name="vehTypeId" value={filters.vehTypeId} onChange={onFilterChange} className={fieldClass} disabled={dropdownLoading}>
                    {vehicleTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Vehicle Sub Type</label>
                  <select name="vehSubTypeId" value={filters.vehSubTypeId} onChange={onFilterChange} className={fieldClass} disabled={dropdownLoading}>
                    {vehicleSubTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Vehicle Basis</label>
                  <select name="vehBasis" value={filters.vehBasis} onChange={onFilterChange} className={fieldClass} disabled={dropdownLoading}>
                    {vehicleBasis.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>

                <div className="relative">
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    value={filters.vehicleCode}
                    onChange={(e) => handleSuggestionInput("code", e.target.value)}
                    className={fieldClass}
                    placeholder="Enter vehicle code"
                    autoComplete="off"
                  />
                  {codeSuggestions.length > 0 && (
                    <div className={suggestionBoxClass}>
                      {codeSuggestions.map((s, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`w-full text-left px-3 py-2 text-sm ${isDark ? "hover:bg-gray-700 text-gray-200" : "hover:bg-blue-50 text-gray-800"}`}
                          onClick={() => selectSuggestion("code", s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="relative">
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    type="text"
                    value={filters.vehicleNumber}
                    onChange={(e) => handleSuggestionInput("number", e.target.value)}
                    className={fieldClass}
                    placeholder="Enter vehicle number"
                    autoComplete="off"
                  />
                  {numSuggestions.length > 0 && (
                    <div className={suggestionBoxClass}>
                      {numSuggestions.map((s, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`w-full text-left px-3 py-2 text-sm ${isDark ? "hover:bg-gray-700 text-gray-200" : "hover:bg-blue-50 text-gray-800"}`}
                          onClick={() => selectSuggestion("number", s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="relative md:col-span-2">
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    value={filters.vehicleName}
                    onChange={(e) => handleSuggestionInput("name", e.target.value)}
                    className={fieldClass}
                    placeholder="Enter vehicle name"
                    autoComplete="off"
                  />
                  {nameSuggestions.length > 0 && (
                    <div className={suggestionBoxClass}>
                      {nameSuggestions.map((s, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`w-full text-left px-3 py-2 text-sm ${isDark ? "hover:bg-gray-700 text-gray-200" : "hover:bg-blue-50 text-gray-800"}`}
                          onClick={() => selectSuggestion("name", s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="overflow-hidden rounded-lg border shadow-lg">
            {pageLoading || gridLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : list.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <table className={`w-full table-fixed ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-left text-sm font-semibold w-16">S.No</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Vehicle Code</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Vehicle Number</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Vehicle Name</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold w-24">View</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {pagedList.map((item, idx) => {
                    const vehicleIdToOpen = item.id ?? item.vehicleId ?? item.vehId ?? "";
                    return (
                      <tr
                        key={`${vehicleIdToOpen}-${idx}`}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * limit + idx + 1}
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium truncate ${isDark ? "text-blue-400" : "text-blue-600"}`}>{item.code || "-"}</td>
                        <td className={`px-4 py-3 text-sm truncate ${isDark ? "text-gray-300" : "text-gray-900"}`}>{item.number || "-"}</td>
                        <td className={`px-4 py-3 text-sm font-medium truncate ${isDark ? "text-white" : "text-gray-900"}`}>{item.name || "-"}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => setViewRow(item)}
                            className="text-blue-500 hover:text-blue-600 text-lg"
                            title="View"
                          >
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {list.length > 0 && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-6">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Showing <b>{(page - 1) * limit + 1}</b>–
                <b>{Math.min(page * limit, totalRecords)}</b> of <b>{totalRecords}</b>
              </div>

              <div className="flex items-center justify-center gap-2">
                <button
                  className={`px-3 py-1 border rounded ${
                    isDark
                      ? "bg-gray-800 text-white border-gray-700"
                      : "bg-white text-gray-700 border-gray-300"
                  } ${page <= 1 || isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  onClick={() => handlePage(page - 1)}
                  disabled={page <= 1 || isBusy}
                >
                  Prev
                </button>
                <span className={`px-3 py-1 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  Page {page} / {totalPages}
                </span>
                <button
                  className={`px-3 py-1 border rounded ${
                    isDark
                      ? "bg-gray-800 text-white border-gray-700"
                      : "bg-white text-gray-700 border-gray-300"
                  } ${page >= totalPages || isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  onClick={() => handlePage(page + 1)}
                  disabled={page >= totalPages || isBusy}
                >
                  Next
                </button>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(toInt(e.target.value, 10));
                    setPage(1);
                  }}
                  className={fieldClass}
                  style={{ width: 130 }}
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {viewRow && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
              onClick={() => setViewRow(null)}
            >
              <div
                className={`w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl border ${
                  isDark
                    ? "bg-gray-900 border-gray-700 text-white"
                    : "bg-white border-gray-200 text-gray-900"
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                  <div>
                    <h3 className="text-xl font-bold">Vehicle Details</h3>
                    <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {viewRow.code || viewRow.name || "-"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setViewRow(null)}
                    className={`p-2 rounded-lg ${isDark ? "hover:bg-gray-800 text-gray-300" : "hover:bg-gray-100 text-gray-600"}`}
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ["Vehicle Main Type", viewRow.mainType],
                    ["Vehicle Code", viewRow.code],
                    ["Vehicle Number", viewRow.number],
                    ["Date", viewRow.date],
                    ["Vehicle Name", viewRow.name],
                    ["Vehicle Type", viewRow.type],
                    ["Vehicle Basis", viewRow.basis],
                    ["Supplier", viewRow.supplier],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className={`p-3 rounded-lg border ${
                        isDark ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        {label}
                      </div>
                      <div className="text-sm font-medium break-words">{value || "-"}</div>
                    </div>
                  ))}
                </div>

                <div className={`flex justify-end gap-2 px-6 py-4 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                  <button
                    type="button"
                    onClick={() => setViewRow(null)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-800"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    disabled={!addRights}
                    onClick={() => {
                      const id = viewRow.id ?? viewRow.vehicleId ?? viewRow.vehId;
                      setViewRow(null);
                      openBillGeneration(id);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${buttonClass} ${hoverClass}`}
                  >
                    Open Bill
                  </button>
                </div>
              </div>
            </div>
          )}

          {isBusy && (
            <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {pageLoading
                ? "Loading page..."
                : dropdownLoading
                ? "Loading dropdown..."
                : gridLoading
                ? "Loading grid..."
                : exporting
                ? "Exporting..."
                : "Loading..."}
            </div>
          )}

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleBillGenerationListing;